import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Breadcrumb } from "@boozallen-uip/uswds-react-core";
import { IndeterminateSpinner } from '@boozallen-uip/uswds-react-loading';
import { PageSubHeader, SectionContainer, SectionSubHeader } from "../shared.styled";
import useAccount from '../../hooks/useAccount.hook';
import PendingUserRequest from './PendingUserRequest.component';
import ApprovedUserRequest from './ApprovedUserRequest.component';
import AccountChangeNotice from './AccountChangeNotice.component';
import { UserContext } from '../UserContext';
import AccessStatus from '../../components/AccessStatus.component';
import {
  HOME_URL,
  ACCESS_DENIED,
  ACCESS_LEVEL_ADMIN,
  ACCESS_LEVEL_SUPER_USER,
  ACCESS_NO_LOGIN,
  ACCESS_WAITING,
  BASE_TITLE,
  ENTITY_APPROVAL_URL
} from '../../utlities/core.constants';
import PageHeader from '../../components/PageHeader/PageHeader.component';
import {
  accessLevelDropdownOptionsForGroup,
  accessLevelDropdownOptionsForRequest,
  displayAccessLevelRoleName,
} from './accessLevelDropdownOptions';

const SUPER_USER_TAB_LABELS = {
  admin: 'Admin Accounts',
  general: 'General User Accounts',
};

const VENDOR_ACCESS_LEVEL_DEFINITIONS = [
  {
    label: 'Vendor Admin',
    description:
      'Can add, edit, publish, and hide products and ACRs, and can manage vendor accounts.'
  },
  {
    label: 'Vendor Editor',
    description:
      'Can add, edit, publish, and hide products and ACRs, but cannot manage vendor accounts.'
  }
];

const BUYER_ACCESS_LEVEL_DEFINITIONS = [
  {
    label: 'Buyer Admin',
    description:
      'Can view all ACRs and products set to public or buyer-only view, and can manage buyer accounts.'
  },
  {
    label: 'Buyer User',
    description:
      'Can view all ACRs and products set to public or buyer-only view, but cannot manage buyer accounts.'
  }
];

const ALL_ACCESS_LEVEL_DEFINITIONS = [
  ...VENDOR_ACCESS_LEVEL_DEFINITIONS,
  ...BUYER_ACCESS_LEVEL_DEFINITIONS
];

const ACCOUNT_SECTION_GUIDE_NOTES = [
  {
    title: 'Pending Requests',
    description: 'New requests that need an approval decision by an Organization Administrator.'
  },
  {
    title: 'Previously Approved Requests',
    description: 'Active accounts, previously approved by Organization Administrators.'
  },
  {
    title: 'Inactive Accounts',
    description: 'Accounts that are no longer active in the ACR Repository site.'
  },
  {
    title: 'Revoked Accounts',
    description: 'Accounts that have been revoked by an Organization Administrator.'
  }
];

const ACTION_GUIDE_NOTES = [
  'Address pending requests using Approve or Deny. Denying an account requires a denial reason be selected.',
  'Update the access level for active accounts by toggling the Access Level field and clicking Save.',
  'Revoke active accounts using Revoke Access. Revoking access requires a reason be selected.',
  'Reactivate inactive and revoked accounts using Reactivate Access. An access level must be selected before reactivating an account.'
];

/**
 * One card per decided account. The API can return more than one decided access-request
 * record for the same user (a self-submitted request plus a Super-User-created one, or one
 * per vendor), which otherwise renders the same account multiple times. Keep the first record
 * per email (case-insensitive); records missing a userName fall back to their id so distinct
 * rows aren't collapsed together.
 *
 * Do not use this on the pending list. Each pending document is a separate decision;
 * collapsing them hides undecided siblings that stay in the database and reappear after the
 * visible request is decided. Denying a leftover can revoke an account that was just approved.
 */
function dedupeByAccount(requests) {
  const seen = new Set();
  return requests.filter((request) => {
    const key = request.userName ? request.userName.toLowerCase() : `id:${request.id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * IsAdmin and the cross-org list both read the account's current database role.
 * Saving a lower level onto the signed-in user's own account makes Undo and the
 * follow-up list load fail: an org admin loses IsAdmin, and a super user dropped
 * below SUPERUSER no longer receives every organization and cannot grant it back.
 */
function savedLevelLocksOutActor(actor, request, nextAccessLevel) {
  const actorEmail = actor?.email?.trim().toLowerCase();
  const targetEmail = request?.userName?.trim().toLowerCase();
  if (!actorEmail || actorEmail !== targetEmail) {
    return false;
  }
  if (actor.accessLevel === ACCESS_LEVEL_SUPER_USER) {
    return nextAccessLevel !== ACCESS_LEVEL_SUPER_USER;
  }
  return nextAccessLevel !== ACCESS_LEVEL_ADMIN;
}

/** Definitions copy for the access-level list; visibility rules are in getAccessLevelDefinitionsForViewer. */
function getAccessLevelDefinitionsForViewer({ isSuperUser, isVendorUser, isAgencyUser }) {
  if (isSuperUser) {
    return ALL_ACCESS_LEVEL_DEFINITIONS;
  }
  if (isVendorUser && !isAgencyUser) {
    return VENDOR_ACCESS_LEVEL_DEFINITIONS;
  }
  if (isAgencyUser && !isVendorUser) {
    return BUYER_ACCESS_LEVEL_DEFINITIONS;
  }
  if (isVendorUser && isAgencyUser) {
    return ALL_ACCESS_LEVEL_DEFINITIONS;
  }
  // TODO: Remove this fallback when session-wide user entity validation guarantees
  // org admins always have either vendor or buyer context (invalid admin-with-no-org should not reach UI).
  return ALL_ACCESS_LEVEL_DEFINITIONS;
}

export default function UserRequestApprover() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('admin');
  const [recentChanges, setRecentChanges] = useState({});
  const [ownAccessNotice, setOwnAccessNotice] = useState(null);
  const [focusRequestId, setFocusRequestId] = useState(null);
  const ownAccessNoticeRef = useRef(null);
  const loadSequence = useRef(0);
  const { getUserAccountRequests, approveUserAccountRequest } = useAccount();
  const {
    hasPendingRequestAccess,
    checkEntityAccessLevel,
    isLoggedIn,
    isSuperUser,
    isVendorUser,
    isAgencyUser,
    isImpersonating,
    realUser,
    setUser,
  } = useContext(UserContext);

  document.title = `${BASE_TITLE} - User Dashboard`;

  // Only the initial load shows the spinner. Refreshes after an action keep the current cards
  // mounted so the admin's scroll position, focus, and confirmation messages survive.
  // The sequence number drops responses from superseded loads (rapid saves, unmount).
  const loadRequests = useCallback(async ({ showSpinner }) => {
    loadSequence.current += 1;
    const sequence = loadSequence.current;
    if (showSpinner) {
      setIsLoading(true);
    }
    const requestsResponse = await getUserAccountRequests(true);
    const approvedRequestsResponse = await getUserAccountRequests(false);
    if (sequence !== loadSequence.current) {
      return;
    }
    setPendingRequests(requestsResponse || []);
    setApprovedRequests(dedupeByAccount(approvedRequestsResponse || []));
    setIsLoading(false);
  }, [getUserAccountRequests]);

  // The list endpoint authorizes the real SuperUser and returns every org. Support Mode must not
  // load it; the nav already hides this page for the same reason.
  const canManageUserAccess = !isImpersonating && checkEntityAccessLevel([ACCESS_LEVEL_ADMIN]);

  useEffect(() => {
    if (!canManageUserAccess) {
      setPendingRequests([]);
      setApprovedRequests([]);
      setIsLoading(false);
      return undefined;
    }

    loadRequests({ showSpinner: true });

    return () => {
      loadSequence.current += 1;
    };
  }, [canManageUserAccess, loadRequests])

  // One-shot: child effects run before this one, so the target card has already focused itself
  // by the time this clears. Without the reset, a later remount of that card would steal focus.
  useEffect(() => {
    if (focusRequestId) {
      setFocusRequestId(null);
    }
  }, [focusRequestId]);

  useEffect(() => {
    if (ownAccessNotice) {
      ownAccessNoticeRef.current?.focus();
    }
  }, [ownAccessNotice]);

  const onChangeHandler = () => {
    loadRequests({ showSpinner: false });
  }

  const accessLevelSavedHandler = ({ request, accessLevel }) => {
    if (savedLevelLocksOutActor(realUser, request, accessLevel)) {
      const roleName = displayAccessLevelRoleName(accessLevel, request);
      const restoreNote = realUser?.accessLevel === ACCESS_LEVEL_SUPER_USER
        ? "This change was to your own super user account, so Manage Users can no longer undo it or reload every organization's accounts. Another super user has to restore the previous access level."
        : "This change was to your own account, so Manage Users can no longer undo it or reload the account list. Another administrator can restore the previous access level.";
      setOwnAccessNotice(`Access level saved: ${roleName}. ${restoreNote}`);
      setUser({ ...realUser, accessLevel });
      return;
    }
    setApprovedRequests((previous) =>
      previous.map((r) => (r.id === request.id ? { ...r, accessLevel } : r))
    );
    setRecentChanges((previous) => ({
      ...previous,
      [request.id]: { previousAccessLevel: request.accessLevel, accessLevel },
    }));
    loadRequests({ showSpinner: false });
  }

  const clearRecentChange = (requestId) => {
    setRecentChanges((previous) => {
      if (!previous[requestId]) {
        return previous;
      }
      const next = { ...previous };
      delete next[requestId];
      return next;
    });
  }

  // Throws on failure so the notice can show its own error and keep the Undo link available.
  const undoAccessLevelChange = async (request) => {
    const { previousAccessLevel } = recentChanges[request.id];
    await approveUserAccountRequest({ requestId: request.id, accessLevel: previousAccessLevel });
    setApprovedRequests((previous) =>
      previous.map((r) => (r.id === request.id ? { ...r, accessLevel: previousAccessLevel } : r))
    );
    clearRecentChange(request.id);
    setFocusRequestId(request.id);
    loadRequests({ showSpinner: false });
  }

  const selectTab = (tab) => {
    if (tab === activeTab) {
      return;
    }
    setActiveTab(tab);
    setRecentChanges({});
  }

  if(!isLoggedIn()){
    return (
      <AccessStatus access={ACCESS_NO_LOGIN} />
    )
  }

  if (ownAccessNotice) {
    return (
      <div>
        <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
          Manage User Access
        </PageHeader>
        <SectionContainer>
          <div ref={ownAccessNoticeRef} tabIndex={-1} role="status">
            <Alert type="success" slim>
              {ownAccessNotice}
            </Alert>
          </div>
        </SectionContainer>
      </div>
    );
  }

  if(!canManageUserAccess) {
    return (
      <AccessStatus access={ACCESS_DENIED} />
    )
  }

  if(hasPendingRequestAccess) {
    return (
      <AccessStatus access={ACCESS_WAITING} />
    )
  }

  const accessLevelDefinitions = getAccessLevelDefinitionsForViewer({
    isSuperUser,
    isVendorUser,
    isAgencyUser
  });

  const pendingAdminRequests = pendingRequests.filter(r => r.requestedAccessLevel === ACCESS_LEVEL_ADMIN);
  const pendingGeneralRequests = pendingRequests.filter(r => r.requestedAccessLevel !== ACCESS_LEVEL_ADMIN);
  // Superuser tabs split accounts by access level, so a saved access-level change always moves
  // the account to the other tab. It stays in the tab it left (rendered as a placeholder note in
  // its original position) until the superuser switches tabs.
  const isAdminLevel = (level) => level === ACCESS_LEVEL_ADMIN;
  const isMovedOutOfTab = (request, isAdminTab) => {
    const change = recentChanges[request.id];
    return Boolean(change)
      && isAdminLevel(change.previousAccessLevel) === isAdminTab
      && isAdminLevel(request.accessLevel) !== isAdminTab;
  };
  const approvedAdminRequests = approvedRequests.filter(r => isAdminLevel(r.accessLevel) || isMovedOutOfTab(r, true));
  const approvedGeneralRequests = approvedRequests.filter(r => !isAdminLevel(r.accessLevel) || isMovedOutOfTab(r, false));
  const splitByAccountStatus = (requests) => {
    const inactive = requests.filter((r) => r.accountStatus === "Inactive");
    const revoked = requests.filter((r) => r.accountStatus === "Revoked");
    const active = requests.filter((r) => r.accountStatus !== "Inactive" && r.accountStatus !== "Revoked");
    return { active, inactive, revoked };
  };

  const approvedAdminSplit = splitByAccountStatus(approvedAdminRequests);
  const approvedGeneralSplit = splitByAccountStatus(approvedGeneralRequests);
  const approvedSplit = splitByAccountStatus(approvedRequests);

  const tabClass = (tab) =>
    `px-4 py-2 font-semibold border-b-4 cursor-pointer ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-base-dark'}`;

  const renderApprovedRequest = (request, key, accessLevelOptions) => (
    <ApprovedUserRequest
      key={key}
      request={request}
      onChange={onChangeHandler}
      onAccessLevelSaved={accessLevelSavedHandler}
      onUndoAccessLevel={undoAccessLevelChange}
      onEdit={clearRecentChange}
      recentChange={recentChanges[request.id]}
      shouldFocusAccessLevel={focusRequestId === request.id}
      accessLevelOptions={accessLevelOptions}
    />
  );

  const renderMovedAccountNotice = (request) => {
    const destinationTab = activeTab === 'admin' ? 'general' : 'admin';
    const displayName = request.name || request.userName;
    const roleName = displayAccessLevelRoleName(request.accessLevel, request);
    return (
      <AccountChangeNotice
        key={`moved-request-${request.id}`}
        className="mb-4"
        onUndo={() => undoAccessLevelChange(request)}
        undoContext={`access level change for ${displayName}`}
      >
        {`Access level saved for ${displayName}: ${roleName}. This account now appears under ${SUPER_USER_TAB_LABELS[destinationTab]}.`}
      </AccountChangeNotice>
    );
  };

  const renderRequestGroup = (requests, type, accessLevelOptions, isApproved) => {
    // Partition on vendor vs buyer so the two groups are mutually exclusive: a record that
    // carries both ids belongs to the vendor group only, otherwise it would render twice
    // (and collide on the request-id React key).
    const filtered = requests.filter(r => type === 'vendor' ? !!r.vendorId : (!!r.agencyId && !r.vendorId));
    const label = type === 'vendor' ? 'Vendor' : 'Buyer';
    if (filtered.length === 0) return null;
    return (
      <div className='mb-6'>
        <h3 className='font-bold text-lg mb-3 border-b border-gray-lighter pb-1'>{label}</h3>
        {filtered.map((request) => {
          if (!isApproved) {
            return <PendingUserRequest key={`pending-request-${request.id}`} request={request} onChange={onChangeHandler} accessLevelOptions={accessLevelOptions} />;
          }
          if (isMovedOutOfTab(request, activeTab === 'admin')) {
            return renderMovedAccountNotice(request);
          }
          return renderApprovedRequest(request, `approved-request-${request.id}`, accessLevelOptions);
        })}
      </div>
    );
  };

  const renderSuperUserTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-10">
          <IndeterminateSpinner type="circle" color="blue" />
        </div>
      );
    }

    const isPendingEmpty = (requests) => requests.filter(r => !!r.vendorId || !!r.agencyId).length === 0;

    if (activeTab === 'admin') {
      return (
        <>
          <SectionSubHeader className='font-normal'>Pending Requests</SectionSubHeader>
          {isPendingEmpty(pendingAdminRequests) ? (
            <p className="italic text-base-dark">No pending requests.</p>
          ) : (
            <>
              {renderRequestGroup(pendingAdminRequests, 'vendor', accessLevelDropdownOptionsForGroup(true), false)}
              {renderRequestGroup(pendingAdminRequests, 'buyer', accessLevelDropdownOptionsForGroup(false), false)}
            </>
          )}
          <SectionSubHeader className='font-normal'>Previously Approved Requests</SectionSubHeader>
          {approvedAdminSplit.active.length === 0 ? (
            <p className="italic text-base-dark">No approved accounts.</p>
          ) : (
            <>
              {renderRequestGroup(approvedAdminSplit.active, 'vendor', accessLevelDropdownOptionsForGroup(true), true)}
              {renderRequestGroup(approvedAdminSplit.active, 'buyer', accessLevelDropdownOptionsForGroup(false), true)}
            </>
          )}
          <SectionSubHeader className='font-normal'>Inactive Accounts</SectionSubHeader>
          {approvedAdminSplit.inactive.length === 0 ? (
            <p className="italic text-base-dark">No inactive accounts.</p>
          ) : (
            <>
              {renderRequestGroup(approvedAdminSplit.inactive, 'vendor', accessLevelDropdownOptionsForGroup(true), true)}
              {renderRequestGroup(approvedAdminSplit.inactive, 'buyer', accessLevelDropdownOptionsForGroup(false), true)}
            </>
          )}
          <SectionSubHeader className='font-normal'>Revoked Accounts</SectionSubHeader>
          {approvedAdminSplit.revoked.length === 0 ? (
            <p className="italic text-base-dark">No revoked accounts.</p>
          ) : (
            <>
              {renderRequestGroup(approvedAdminSplit.revoked, 'vendor', accessLevelDropdownOptionsForGroup(true), true)}
              {renderRequestGroup(approvedAdminSplit.revoked, 'buyer', accessLevelDropdownOptionsForGroup(false), true)}
            </>
          )}
        </>
      );
    }
    return (
      <>
        <SectionSubHeader className='font-normal'>Pending Requests</SectionSubHeader>
        {isPendingEmpty(pendingGeneralRequests) ? (
          <p className="italic text-base-dark">No pending requests.</p>
        ) : (
          <>
            {renderRequestGroup(pendingGeneralRequests, 'vendor', accessLevelDropdownOptionsForGroup(true), false)}
            {renderRequestGroup(pendingGeneralRequests, 'buyer', accessLevelDropdownOptionsForGroup(false), false)}
          </>
        )}
        <SectionSubHeader className='font-normal'>Previously Approved Requests</SectionSubHeader>
        {approvedGeneralSplit.active.length === 0 ? (
          <p className="italic text-base-dark">No approved accounts.</p>
        ) : (
          <>
            {renderRequestGroup(approvedGeneralSplit.active, 'vendor', accessLevelDropdownOptionsForGroup(true), true)}
            {renderRequestGroup(approvedGeneralSplit.active, 'buyer', accessLevelDropdownOptionsForGroup(false), true)}
          </>
        )}
        <SectionSubHeader className='font-normal'>Inactive Accounts</SectionSubHeader>
        {approvedGeneralSplit.inactive.length === 0 ? (
          <p className="italic text-base-dark">No inactive accounts.</p>
        ) : (
          <>
            {renderRequestGroup(approvedGeneralSplit.inactive, 'vendor', accessLevelDropdownOptionsForGroup(true), true)}
            {renderRequestGroup(approvedGeneralSplit.inactive, 'buyer', accessLevelDropdownOptionsForGroup(false), true)}
          </>
        )}
        <SectionSubHeader className='font-normal'>Revoked Accounts</SectionSubHeader>
        {approvedGeneralSplit.revoked.length === 0 ? (
          <p className="italic text-base-dark">No revoked accounts.</p>
        ) : (
          <>
            {renderRequestGroup(approvedGeneralSplit.revoked, 'vendor', accessLevelDropdownOptionsForGroup(true), true)}
            {renderRequestGroup(approvedGeneralSplit.revoked, 'buyer', accessLevelDropdownOptionsForGroup(false), true)}
          </>
        )}
      </>
    );
  };

  return(
  <div>
    <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
      Manage User Access
      </PageHeader>

  <SectionContainer>
    <Breadcrumb links={[{"href": HOME_URL, "text": "Home"}, {"href": ENTITY_APPROVAL_URL, "text": "Approval"}]} />
    <PageSubHeader>Manage Account Access</PageSubHeader>
    <p>Welcome to your admin dashboard. This is where you will find incoming requests from users from your organization. </p>
    <details open className="mt-4 mb-6 border border-gray-lighter rounded-md bg-base-lightest">
      <summary className="cursor-pointer px-4 py-3 text-base font-bold text-primary-darker">
        Page Guide
      </summary>
      <div className="px-4 pb-4 pt-1">
        <h2 className="text-base font-bold mb-2 text-primary-darker">Access Level Types</h2>
        <ul className='pl-6 mb-4 [&>li]:leading-normal'>
          {accessLevelDefinitions.map(({ label, description }) => (
            <li key={label}>
              <span className="font-bold">{label}:</span> {description}
            </li>
          ))}
        </ul>
        <h2 className="text-base font-bold mb-2 text-primary-darker">Account Status Types</h2>
        <ul className='pl-6 mb-4 [&>li]:leading-normal'>
          {ACCOUNT_SECTION_GUIDE_NOTES.map(({ title, description }) => (
            <li key={title}>
              <span className="font-bold">{title}:</span> {description}
            </li>
          ))}
        </ul>
        <h2 className="text-base font-bold mb-2 text-primary-darker">Administrator Capabilities</h2>
        <ul className='pl-6 mb-4 [&>li]:leading-normal'>
          {ACTION_GUIDE_NOTES.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </div>
    </details>
    {isSuperUser ? (
      <>
        <div className="flex border-b border-gray-lighter mb-6">
          <button type="button" className={tabClass('admin')} onClick={() => selectTab('admin')}>{SUPER_USER_TAB_LABELS.admin}</button>
          <button type="button" className={`ml-4 ${tabClass('general')}`} onClick={() => selectTab('general')}>{SUPER_USER_TAB_LABELS.general}</button>
        </div>
        <section>
          {renderSuperUserTabContent()}
        </section>
      </>
    ) : (
      <section>
        {isLoading ? (
          <div className="flex justify-center items-center py-10">
            <IndeterminateSpinner type="circle" color="blue" />
          </div>
        ) : (
          <>
            <SectionSubHeader className='font-normal'>Pending Requests</SectionSubHeader>
            <div>
              {pendingRequests.length === 0 ? (
                <p className="italic text-base-dark">No pending requests.</p>
              ) : pendingRequests.map((request) => (
                <PendingUserRequest
                  key={`pending-request-${request.id}`}
                  request={request}
                  onChange={onChangeHandler}
                  accessLevelOptions={accessLevelDropdownOptionsForRequest(request)}
                />
              ))}
            </div>
            <SectionSubHeader className='font-normal'>Previously Approved Requests</SectionSubHeader>
            <div>
              {approvedSplit.active.length === 0 ? (
                <p className="italic text-base-dark">No approved accounts.</p>
              ) : approvedSplit.active.map((request) => renderApprovedRequest(
                request,
                `approved-request-${request.id}`,
                accessLevelDropdownOptionsForRequest(request)
              ))}
            </div>
            <SectionSubHeader className='font-normal'>Inactive Accounts</SectionSubHeader>
            <div>
              {approvedSplit.inactive.length === 0 ? (
                <p className="italic text-base-dark">No inactive accounts.</p>
              ) : approvedSplit.inactive.map((request) => renderApprovedRequest(
                request,
                `inactive-request-${request.id}`,
                accessLevelDropdownOptionsForRequest(request)
              ))}
            </div>
            <SectionSubHeader className='font-normal'>Revoked Accounts</SectionSubHeader>
            <div>
              {approvedSplit.revoked.length === 0 ? (
                <p className="italic text-base-dark">No revoked accounts.</p>
              ) : approvedSplit.revoked.map((request) => renderApprovedRequest(
                request,
                `revoked-request-${request.id}`,
                accessLevelDropdownOptionsForRequest(request)
              ))}
            </div>
          </>
        )}
      </section>
    )}
  </SectionContainer>
  </div>);
}
